<template>
  <div class="flex flex-col w-full h-full overflow-hidden">
    <div class="w-full flex gap-2 items-center shrink-0">
      <IconField class="flex-grow">
        <InputIcon class="pi pi-search" />
        <InputText size="small" v-model="searchValue" placeholder="חיפוש" class="w-full" />
      </IconField>
      <Button
        v-if="canScanBarcode"
        icon="pi pi-barcode"
        size="small"
        outlined
        @click="scanProduct" />
    </div>
    <div class="flex-1 min-h-0 overflow-y-auto flex flex-col gap-2 py-4">
      <product-item v-for="cartItem of cart"
                    :product="cartItem.item"
                    @change-amount="({ itemId, amount }) => { changeItemAmountInCart(itemId, amount)}"
                    :amount="cartItem.amount"
                    mode="checkout" />
      <product-item v-for="product of allAvailableProducts"
                    :product="product"
                    @change-amount="({ itemId, amount }) => { changeItemAmountInCart(itemId, amount)}"
                    mode="checkout" />
    </div>

    <div class="flex justify-between items-center shrink-0 pt-2">
      <span>סה״כ לתשלום</span>
      <span> ₪{{ cartSum }}</span>
    </div>


    <drawer-footer
      class="shrink-0"
      :approveLoading="loading"
      approveLabel="עדכון תור"
      cancelLabel="חזור"
      @approve-click="saveChanges"
      @cancel-click="$emit('close')"
    />
  </div>

</template>

<script setup lang="ts">
import type { IShopItem, TAppointmentItem } from '@torim-app/types';

import { computed, PropType, ref, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { useAdminHomeStore, useAdminStore } from '@app/stores';
import { AppointmentsApi } from '@app/api/admin';
import InputIcon from 'primevue/inputicon';
import IconField from 'primevue/iconfield';
import { ProductItem } from '@torim-app/shared-components';
import { trackEvent } from '@app/composables/posthog';
import { Capacitor } from '@capacitor/core';
import { PRODUCT_BARCODE_FORMATS, useQRScanner } from '@app/composables/qr-reader';

const props = defineProps({
  apid: { type: String, required: true },
  cartProducts: { type: Object as PropType<Array<TAppointmentItem & { item: IShopItem }>>, required: true }
});
const emit = defineEmits(['complete', 'close']);

const toast = useToast();
const adminStore = useAdminStore();
const adminHomeStore = useAdminHomeStore();

const loading = ref<boolean>(false);
const cart = ref<Array<TAppointmentItem & { item: IShopItem }>>([...props.cartProducts.map(x => ({ ...x }))]);
const planToAdd = ref<IShopItem>();

const searchValue = ref<string>();

const allAvailableProducts = computed(() => {
  const allProducts = adminStore.businessSiteData?.shop?.items?.filter(x => x.active && x.availableStock && x.availableStock > 0) ?? [];
  return allProducts.filter(x => !cart.value.find(y => y.item.id === x.id));
});
const cartSum = computed(() => (cart.value ?? [])?.reduce((acc, cur) => acc += cur.price * cur.amount, 0));

const addItemToCart = (item: IShopItem) => {
  const itemExist = cart.value.find(x => x.id === item.id);
  if (itemExist) return;

  const itemToPush: TAppointmentItem & { item: IShopItem } = {
    id: item.id,
    amount: 1,
    type: 'product',
    name: '',
    price: item.discountedPrice ?? item.price,
    item
  };
  cart.value.push(itemToPush);
};

const removeItemFromCart = (itemId: string) => {
  const cartItems = [...cart.value];
  cart.value.splice(0);
  cart.value.push(...cartItems.filter(x => x.id !== itemId));
};

const { scanSingleBarcode } = useQRScanner();
const canScanBarcode = Capacitor.isNativePlatform();

// Supermarket-style: each scan adds one unit of the matching product.
const scanProduct = async () => {
  trackEvent('edit_cart_barcode_scan_clicked');
  try {
    const barcode = await scanSingleBarcode(PRODUCT_BARCODE_FORMATS);
    const product = (adminStore.businessSiteData?.shop?.items ?? [])
      .find(x => x.active && x.barcode && x.barcode === barcode);
    if (!product) {
      toast.warning('לא נמצא מוצר עם הברקוד הזה');
      return;
    }
    if (product.availableStock !== undefined && product.availableStock !== null && product.availableStock <= 0) {
      toast.warning(`${product.name} אזל מהמלאי`);
      return;
    }
    if (cart.value.find(x => x.id === product.id))
      changeItemAmountInCart(product.id, 1);
    else
      addItemToCart(product);
    toast.success(`${product.name} נוסף לעגלה`);
  } catch (e) {
    console.error(e);
    toast.error('הסריקה נכשלה, אפשר לחפש את המוצר ידנית');
  }
};

const changeItemAmountInCart = (itemId: string, amount: -1 | 1) => {
  let itemToUpdate = cart.value.find(x => x.id === itemId);
  if (!itemToUpdate) {
    addItemToCart(allAvailableProducts.value.find(x => x.id === itemId));
  } else {
    itemToUpdate.amount += amount;
    itemToUpdate.price = itemToUpdate.item.discountedPrice ?? itemToUpdate.item.price;
    if (itemToUpdate.amount === 0)
      removeItemFromCart(itemId);
  }
};

const saveChanges = () => {
  trackEvent('cart_edited');
  loading.value = true;
  const items = cart.value.filter(x => x.amount > 0).map<TAppointmentItem>(x => ({
    price: x.price,
    amount: x.amount,
    id: x.id,
    name: '',
    type: 'product'
  }));
  AppointmentsApi.updateCartItemsInAppointment(props.apid, items)
    .then(async () => {
      toast.success('המוצרים המצורפים לתור עודכנו!');
      await adminHomeStore.fetchBusinessCalendarDataSilently();
      emit('complete');
    })
    .catch(() => {
      toast.error('לא הצלחנו לעדכן את המוצרים המצורפים לתור, אנא נסו שוב');
    })
    .finally(() => {
      loading.value = false;
    });
};

watch(() => planToAdd.value, (value) => {
  if (value) {
    addItemToCart(value);
    planToAdd.value = undefined;
  }
});

</script>

<style scoped lang="scss">

</style>
